// ============================================================
// UsersPage.js
// Global list of all ClientUsers across all Clients/Tenants.
// Filters: Client + Role + Include inactive. Row click navigates
// to /users/:clientUserId (ComingSoonPage stub for now — User
// detail lands in a later session).
//
// URL state:
//   ?page=N, ?pageSize=N, ?clientId=N, ?roleKey=X,
//   ?includeInactive=true, ?requiresSetupOnly=true
//
// Backend supports a `search` substring param against
// Email / DisplayName. The admin UI does not yet expose a search
// input — surfaced as a flag in Session 2b notes. Add the input
// alongside the dropdowns if/when search is added to the UI
// requirements.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { UserPlus } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import { RoleBadge, ActiveStatusBadge, SetupRequiredBadge } from '../components/StatusBadge';
import { listAdminUsers, listAdminClients } from '../api/adminClients';
import { translateError } from '../utils/errorMessages';
import { formatDate } from '../utils/format';
import './UsersPage.css';

const DEFAULT_PAGE_SIZE = 50;

const ROLE_FILTER_OPTIONS = [
  { value: '',        label: 'All roles' },
  { value: 'manager', label: 'Manager' },
  { value: 'carer',   label: 'Carer' },
  { value: 'admin',   label: 'Admin' },
  { value: 'auditor', label: 'Auditor' },
];

function parsePageParam(raw, fallback) {
  if (raw == null) return fallback;
  const n = parseInt(raw, 10);
  return Number.isInteger(n) && n > 0 ? n : fallback;
}

export default function UsersPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const page              = parsePageParam(searchParams.get('page'), 1);
  const pageSize          = parsePageParam(searchParams.get('pageSize'), DEFAULT_PAGE_SIZE);
  const clientId          = searchParams.get('clientId') || '';
  const roleKey           = searchParams.get('roleKey') || '';
  const includeInactive   = searchParams.get('includeInactive') === 'true';
  const requiresSetupOnly = searchParams.get('requiresSetupOnly') === 'true';

  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Independent Client-dropdown fetch. Fails soft so a broken
  // /clients endpoint doesn't disable the entire page.
  const [clients, setClients] = useState([]);
  useEffect(() => {
    let cancelled = false;
    listAdminClients({ pageSize: 200 })
      .then((res) => { if (!cancelled) setClients(res.items || []); })
      .catch(() => { /* fall back to "All clients" only */ });
    return () => { cancelled = true; };
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, pageSize };
      if (clientId)          params.clientId = parseInt(clientId, 10);
      if (roleKey)           params.roleKey = roleKey;
      if (includeInactive)   params.includeInactive = true;
      if (requiresSetupOnly) params.requiresSetupOnly = true;
      const res = await listAdminUsers(params);
      setItems(res.items || []);
      setTotalCount(res.totalCount || 0);
    } catch (err) {
      setError(translateError(err));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, clientId, roleKey, includeInactive, requiresSetupOnly]);

  useEffect(() => { fetchData(); }, [fetchData]);

  function handleClientChange(value) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set('clientId', value); else next.delete('clientId');
      next.delete('page');
      return next;
    });
  }

  function handleRoleChange(value) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set('roleKey', value); else next.delete('roleKey');
      next.delete('page');
      return next;
    });
  }

  function handleIncludeInactiveChange(checked) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (checked) next.set('includeInactive', 'true'); else next.delete('includeInactive');
      next.delete('page');
      return next;
    });
  }

  function handleRequiresSetupOnlyChange(checked) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (checked) next.set('requiresSetupOnly', 'true'); else next.delete('requiresSetupOnly');
      next.delete('page');
      return next;
    });
  }

  function handlePageChange(nextPage) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (nextPage === 1) next.delete('page'); else next.set('page', String(nextPage));
      return next;
    });
  }

  function clearFilters() {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('clientId');
      next.delete('roleKey');
      next.delete('includeInactive');
      next.delete('requiresSetupOnly');
      next.delete('page');
      return next;
    });
  }

  const columns = [
    {
      key: 'email',
      label: 'Email',
      mobile: 'title',
      render: (row) => <span className="users-page__email">{row.email}</span>,
    },
    {
      key: 'displayName',
      label: 'Display Name',
      mobile: 'subtitle',
      render: (row) => (
        <span className={row.displayName ? '' : 'users-page__muted'}>
          {row.displayName || '—'}
        </span>
      ),
    },
    {
      key: 'role',
      label: 'Role',
      render: (row) => <RoleBadge roleKey={row.roleKey} />,
      width: '120px',
    },
    {
      key: 'setup',
      label: 'Setup',
      render: (row) => <SetupRequiredBadge requiresAgentSetup={row.requiresAgentSetup} />,
      width: '140px',
    },
    {
      key: 'client',
      label: 'Client',
      render: (row) => <span className="users-page__client">{row.clientName}</span>,
      width: '180px',
    },
    {
      key: 'tenant',
      label: 'Tenant',
      render: (row) => (
        <span className={row.tenantName ? 'users-page__tenant' : 'users-page__muted'}>
          {row.tenantName || '—'}
        </span>
      ),
      width: '160px',
    },
    {
      key: 'status',
      label: 'Status',
      mobile: 'badge',
      render: (row) => <ActiveStatusBadge isActive={row.isActive} />,
      width: '100px',
    },
    {
      key: 'accessExpires',
      label: 'Access expires',
      render: (row) => (
        <span className="users-page__muted">
          {row.accessExpiresAt ? formatDate(row.accessExpiresAt) : '—'}
        </span>
      ),
      width: '140px',
    },
    {
      key: 'createdAt',
      label: 'Created',
      render: (row) => (
        <span className="users-page__muted">{formatDate(row.createdAt)}</span>
      ),
      width: '130px',
    },
  ];

  function handleRowClick(row) {
    navigate(`/users/${row.clientUserId}`);
  }

  const isFiltered = Boolean(clientId || roleKey || includeInactive || requiresSetupOnly);
  const hasNoResults = !loading && !error && items.length === 0;
  const emptyMessage = isFiltered
    ? 'No users match these filters.'
    : 'No users yet. Invite the first user to get started.';

  return (
    <>
      <PageHeader
        title="Users"
        subtitle="All users across all Clients."
        cta={
          <button
            type="button"
            className="users-page__invite"
            onClick={() => navigate('/invitations/new')}
          >
            <UserPlus size={16} />
            Invite User
          </button>
        }
      />

      <div className="users-page__toolbar">
        <label className="users-page__filter">
          <span className="users-page__filter-label">Client</span>
          <select
            value={clientId}
            onChange={(e) => handleClientChange(e.target.value)}
            className="users-page__select"
          >
            <option value="">All clients</option>
            {clients.map((c) => (
              <option key={c.clientId} value={String(c.clientId)}>{c.clientName}</option>
            ))}
          </select>
        </label>

        <label className="users-page__filter">
          <span className="users-page__filter-label">Role</span>
          <select
            value={roleKey}
            onChange={(e) => handleRoleChange(e.target.value)}
            className="users-page__select"
          >
            {ROLE_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </label>

        <label className="users-page__checkbox">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => handleIncludeInactiveChange(e.target.checked)}
          />
          <span>Include inactive</span>
        </label>

        <label className="users-page__checkbox">
          <input
            type="checkbox"
            checked={requiresSetupOnly}
            onChange={(e) => handleRequiresSetupOnlyChange(e.target.checked)}
          />
          <span>Show only users requiring setup</span>
        </label>
      </div>

      <DataTable
        data={items}
        columns={columns}
        rowKey={(row) => row.clientUserId}
        onRowClick={handleRowClick}
        totalCount={totalCount}
        page={page}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        loading={loading}
        error={error}
        onRetry={fetchData}
        emptyMessage={emptyMessage}
        emptyAction={
          hasNoResults ? (
            isFiltered ? (
              <button
                type="button"
                className="users-page__clear-filters"
                onClick={clearFilters}
              >
                Clear filters
              </button>
            ) : (
              <button
                type="button"
                className="users-page__invite-empty"
                onClick={() => navigate('/invitations/new')}
              >
                <UserPlus size={16} />
                Invite first user
              </button>
            )
          ) : null
        }
      />
    </>
  );
}
